import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading, Modal } from "src/components/common";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { ModalSize } from "src/enumerations/ModalSize";
import { LaboratoryDepartmentsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "src/helpers/date";
import useNavigate from "src/hooks/useNavigate";
import * as LaboratoryActions from "../../../actions/laboratories";
import * as LaboratoryDepartmentActions from "../../../actions/laboratoryDepartments";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { LaboratorySubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./LabDepartmentsPage.scss";

interface LabDepartmentsPageProps {
    laboratory: Dtos.Laboratory;
    loadingLaboratory: boolean;
    loadLaboratorySuccess: boolean;
    loadLaboratoryFailure: boolean;
    loadLaboratoryErrorCode: string;
    laboratoryDepartments: Dtos.LaboratoryDepartment[];
    loadingLaboratoryDepartments: boolean;
    loadLaboratoryDepartmentsSuccess: boolean;
    loadLaboratoryDepartmentsFailure: boolean;
    loadLaboratoryDepartmentsErrorCode: string;
    deletingLaboratoryDepartment: boolean;
    deleteLaboratoryDepartmentSuccess: boolean;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearLaboratory: LaboratoryActions.ILaboratoryClearActionCreator;
    loadLaboratory: LaboratoryActions.ILaboratoryLoadByIdActionCreator;
    clearLaboratoryDepartments: LaboratoryDepartmentActions.ILaboratoryDepartmentClearActionCreator;
    loadLaboratoryDepartments: LaboratoryDepartmentActions.ILaboratoryDepartmentsLoadByLaboratoryIdActionCreator;
    deleteLaboratoryDepartment: LaboratoryDepartmentActions.ILaboratoryDepartmentDeleteActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const LabDepartmentsPage: FC<LabDepartmentsPageProps> = ({
    laboratory,
    loadingLaboratory,
    loadLaboratorySuccess,
    loadLaboratoryFailure,
    loadLaboratoryErrorCode,
    laboratoryDepartments,
    loadingLaboratoryDepartments,
    loadLaboratoryDepartmentsSuccess,
    loadLaboratoryDepartmentsFailure,
    loadLaboratoryDepartmentsErrorCode,
    deletingLaboratoryDepartment,
    deleteLaboratoryDepartmentSuccess,
    permissions,
    pageSize,
    clearLaboratory,
    loadLaboratory,
    clearLaboratoryDepartments,
    loadLaboratoryDepartments,
    deleteLaboratoryDepartment,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Params --
    const { laboratoryId } = useParams<{ laboratoryId: string }>();
    const laboratoryIdNumber = parseInt(laboratoryId, 10);

    //
    // -- States --
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [labDepartment, setLabDepartment] = useState<Dtos.LaboratoryDepartment | undefined>(undefined);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showDepartments = 
        !loadingLaboratory && 
        loadLaboratorySuccess && 
        !loadingLaboratoryDepartments && 
        loadLaboratoryDepartmentsSuccess;


    // Load Laboratory
    useEffect(() => {
        if (laboratoryIdNumber && !laboratory) {
            setDrawerItems(LaboratorySubMenu(laboratoryIdNumber, permissions));
            setBreadcrumbs(
                LaboratoryDepartmentsBreadcrumbs(laboratoryIdNumber)
            );

            loadLaboratory(laboratoryIdNumber);
            loadLaboratoryDepartments(laboratoryIdNumber);
        }
    }, [
        laboratoryIdNumber,
        laboratory,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadLaboratory,
        loadLaboratoryDepartments
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadLaboratoryFailure) {
            navigate(`/error/${loadLaboratoryErrorCode}`);
        }
        if (loadLaboratoryDepartmentsFailure) {
            navigate(`/error/${loadLaboratoryDepartmentsErrorCode}`);
        }
    }, [
        loadLaboratoryFailure, 
        loadLaboratoryErrorCode, 
        loadLaboratoryDepartmentsFailure, 
        loadLaboratoryDepartmentsErrorCode, 
        navigate
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (laboratoryIdNumber && laboratory?.name) {
            setBreadcrumbs(
                LaboratoryDepartmentsBreadcrumbs(
                    laboratoryIdNumber,
                    laboratory.name
                )
            );
        }
    }, [
        laboratory,
        laboratoryIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
    ]);

    // Cleanup effect to clear the laboratory on component unmount
    useEffect(() => {
        return () => {
            clearLaboratory();
            clearLaboratoryDepartments();
        };
    }, [clearLaboratory, clearLaboratoryDepartments]);

    // Reload laboratory after delete
    useEffect(() => {
        if (
            !deletingLaboratoryDepartment &&
            deleteLaboratoryDepartmentSuccess
        ) {
            loadLaboratory(laboratoryIdNumber);
            loadLaboratoryDepartments(laboratoryIdNumber);
        }
    }, [
        deletingLaboratoryDepartment,
        deleteLaboratoryDepartmentSuccess,
        loadLaboratory,
        loadLaboratoryDepartments,
        laboratoryIdNumber,
    ]);

    const generateDeleteButton = (departmentLab: Dtos.LaboratoryDepartment) => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.LabDepartmentUpdate,
            undefined,
            undefined
        );

        if (!hasPermission) return null;

        return (
            <button
                key={`labDeleteButton-${departmentLab.id}`}
                className="btn btn-outline-secondary btn-sm btn-row-version"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setLabDepartment(departmentLab);
                    setOpenModal(true);
                }}
            >
                <FontAwesomeIcon
                    icon={FontAwesomeIcons.Duotone.TRASH}
                    fixedWidth
                />
            </button>
        );
    };

    const createTable = useMemo(() => {
        if (!laboratoryDepartments || laboratoryDepartments?.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no pathology departments to
                            view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination =
            laboratoryDepartments?.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={laboratoryDepartments}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination
                            ? pageSize
                            : laboratoryDepartments?.length
                    }
                    pageSize={
                        showPagination
                            ? pageSize
                            : laboratoryDepartments?.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no pathology departments to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/labs/${laboratoryIdNumber}/departments/${rowInfo.original.id}/details`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.BUILDINGMEMO}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "pathologyDepartment.name",
                            Header: "Pathology Department",
                            accessor: "pathologyDepartment.name",
                        },
                        {
                            id: "expiryDate",
                            Header: "Expiry Date",
                            accessor: "expiryDate",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "deleteDepartment",
                            Header: " ",
                            maxWidth: 75,
                            Cell: (props) => (
                                <div>
                                    {generateDeleteButton(props.original)}
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [laboratoryDepartments, pageSize, PAGE_SIZE_OPTIONS, navigate, setPageSize]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-6 mb-2"}>
                        <h1>Departments</h1>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.LabDepartmentCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/labs/${laboratoryIdNumber}/departments/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showDepartments ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showDepartments ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [AuthenticationHelper, permissions, showDepartments, createTable]);

    const renderDeleteConfirmation = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.LabDepartmentUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            return (
                <Modal
                    open={openModal}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setOpenModal(false);
                        setLabDepartment(undefined);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h2 className="mb-0">Delete</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setOpenModal(false);
                                setLabDepartment(undefined);
                            }}
                        >
                            <FontAwesomeIcon
                                fixedWidth
                                icon={FontAwesomeIcons.Regular.TIMES}
                            />
                        </button>
                    </div>
                    <div className="modal-body">
                        <span>
                            Are you sure you want to delete the pathology
                            department "
                            <em>{labDepartment?.pathologyDepartment?.name}"</em>{" "}
                            from laboratory <strong>{laboratory?.name}</strong>.
                        </span>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                deleteLaboratoryDepartment(labDepartment?.id);
                                setOpenModal(false);
                                setLabDepartment(undefined);
                            }}
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                                setOpenModal(false);
                                setLabDepartment(undefined);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        labDepartment,
        openModal,
        permissions,
        setOpenModal,
        labDepartment,
        setLabDepartment,
        laboratoryIdNumber,
        loadLaboratory,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.LabDepartmentView}
            loading={loadingLaboratory || loadingLaboratoryDepartments}
        >
            {renderContent}
            {renderDeleteConfirmation}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<LabDepartmentsPageProps> => {
    let laboratory: Dtos.Laboratory | undefined = !(
        state.laboratories.formData instanceof Array
    )
        ? state.laboratories.formData
        : undefined;
    let laboratoryDepartments: Dtos.LaboratoryDepartment[] | undefined = (
        state.laboratoryDepartments.formData instanceof Array
    )
        ? state.laboratoryDepartments.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        laboratory: laboratory!,
        loadingLaboratory:
            state.laboratories.loadState &&
            state.laboratories.loadState.status == RequestState.Pending,
        loadLaboratorySuccess:
            state.laboratories.loadState &&
            state.laboratories.loadState.status == RequestState.Success,
        loadLaboratoryFailure:
            state.laboratories.loadState &&
            state.laboratories.loadState.status == RequestState.Failure,
        loadLaboratoryErrorCode: state.laboratories.loadState.errorCode,
        laboratoryDepartments: laboratoryDepartments!,
        loadingLaboratoryDepartments:
            state.laboratoryDepartments.loadState &&
            state.laboratoryDepartments.loadState.status == RequestState.Pending,
        loadLaboratoryDepartmentsSuccess:
            state.laboratoryDepartments.loadState &&
            state.laboratoryDepartments.loadState.status == RequestState.Success,
        loadLaboratoryDepartmentsFailure:
            state.laboratoryDepartments.loadState &&
            state.laboratoryDepartments.loadState.status == RequestState.Failure,
        loadLaboratoryDepartmentsErrorCode: state.laboratoryDepartments.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        deletingLaboratoryDepartment:
            state.laboratoryDepartments.deleteState &&
            state.laboratoryDepartments.deleteState.status ==
                RequestState.Pending,
        deleteLaboratoryDepartmentSuccess:
            state.laboratoryDepartments.deleteState &&
            state.laboratoryDepartments.deleteState.status ==
                RequestState.Success,
    };
};

const mapDispatchToProps = (dispatch): Partial<LabDepartmentsPageProps> => {
    return {
        loadLaboratory: bindActionCreators(
            LaboratoryActions.LoadLaboratoryById,
            dispatch
        ),
        clearLaboratory: bindActionCreators(LaboratoryActions.Clear, dispatch),
        loadLaboratoryDepartments: bindActionCreators(LaboratoryDepartmentActions.LoadLaboratoryDepartmentsByLaboratoryId, dispatch),
        clearLaboratoryDepartments: bindActionCreators(LaboratoryDepartmentActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        deleteLaboratoryDepartment: bindActionCreators(
            LaboratoryDepartmentActions.DeleteLaboratoryDepartment,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(LabDepartmentsPage);
